// ---------------------------------------------------------------------------
// Prezio Onboard Widget — useStepOptions hook
// ---------------------------------------------------------------------------
// Fetches and caches options for a given step.
// Returns cached data immediately if available.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiError, StepId, StepOptionsResponse } from '@/api/types';
import { useSessionState, useSessionActions } from '@/context/SessionContext';

interface UseStepOptionsReturn {
  options: StepOptionsResponse | null;
  isLoading: boolean;
  error: ApiError | null;
  refetch: () => void;
}

export function useStepOptions(step: StepId): UseStepOptionsReturn {
  const { optionsCache } = useSessionState();
  const { fetchOptions } = useSessionActions();

  const cached = optionsCache.get(step) ?? null;

  // Hold the last successfully-loaded options so that cache invalidation
  // (INVALIDATE_CACHE_AFTER during back-nav) doesn't null-out options for
  // a component still visible during AnimatePresence exit animation.
  const optionsRef = useRef<StepOptionsResponse | null>(cached);
  if (cached !== null) {
    optionsRef.current = cached;
  }

  const [isLoading, setIsLoading] = useState(!cached);
  const [error, setError] = useState<ApiError | null>(null);

  // Use a ref to avoid re-running the effect when optionsCache ref changes
  const optionsCacheRef = useRef(optionsCache);
  optionsCacheRef.current = optionsCache;

  const load = useCallback(async () => {
    // Already in cache — skip
    if (optionsCacheRef.current.has(step)) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      await fetchOptions(step);
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setIsLoading(false);
    }
  }, [step, fetchOptions]);

  useEffect(() => {
    void load();
  }, [load]);

  const refetch = useCallback(() => {
    void load();
  }, [load]);

  return { options: optionsRef.current, isLoading, error, refetch };
}
